import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fmtMXN } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Camera, Check, Loader2, ChevronRight, Search, Lock, X, ImageIcon } from "lucide-react";
import { toast } from "sonner";

/**
 * Mobile-first physical progress capture for the residente.
 * - Big slider (0-100)
 * - +5 / +10 quick buttons
 * - Optional photo capture (rear camera)
 * - Only allows INCREASING progress (rule b)
 * - One item at a time; tap an item card from the list to capture
 */
export default function ProjectProgressMobile() {
  const { id: projectId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const [p, b] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/projects/${projectId}/budget`),
      ]);
      setProject(p.data);
      const onlyItems = (b.data.items || []).filter((it) => it.level === "item");
      setItems(onlyItems);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cargar el proyecto");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const filtered = items.filter((it) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (it.description || "").toLowerCase().includes(s) ||
      (it.code || "").toLowerCase().includes(s)
    );
  });

  // KPI summary
  const totalItems = items.length;
  const completed = items.filter((it) => (it.progress_pct_manual || 0) >= 100).length;
  const inProgress = items.filter((it) => {
    const p = it.progress_pct_manual || 0;
    return p > 0 && p < 100;
  }).length;
  const notStarted = items.filter((it) => !it.progress_pct_manual).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0a0f1d" }}>
        <Loader2 className="w-6 h-6 animate-spin text-cyan-300" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#0a0f1d", paddingBottom: "env(safe-area-inset-bottom)" }}>
      {/* Top bar */}
      <div
        className="sticky top-0 z-30 px-4 py-3 flex items-center gap-3"
        style={{ background: "rgba(5,8,18,0.92)", backdropFilter: "blur(8px)", borderBottom: "1px solid var(--border)" }}
      >
        <button
          data-testid="progress-back-btn"
          onClick={() => nav(`/projects/${projectId}/budget`)}
          className="btn-ghost p-2 rounded-sm"
          aria-label="Volver"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="label-mono text-[10px]" style={{ color: "var(--cyan)" }}>// AVANCE FÍSICO · MÓVIL</div>
          <div className="font-display font-bold text-slate-100 text-base truncate">
            {project?.code || "—"} · {project?.name || ""}
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="px-4 pt-4 grid grid-cols-3 gap-2">
        <KpiMini label="Completadas" value={completed} total={totalItems} color="#16a34a" />
        <KpiMini label="En progreso" value={inProgress} total={totalItems} color="#22d3ee" />
        <KpiMini label="Sin iniciar" value={notStarted} total={totalItems} color="#94a3b8" />
      </div>

      {/* Search */}
      <div className="px-4 mt-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            data-testid="progress-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar partida (código o descripción)…"
            className="w-full input-cyber pl-9 pr-3 py-2.5 text-sm"
          />
        </div>
      </div>

      {/* Item list */}
      <div className="px-4 pt-4 pb-6 space-y-2">
        {filtered.length === 0 && (
          <div className="text-center py-12 text-slate-500 text-sm">
            {items.length === 0
              ? "Este proyecto aún no tiene partidas de presupuesto cargadas."
              : "Sin coincidencias. Intenta otra búsqueda."}
          </div>
        )}
        {filtered.map((it) => (
          <ItemCard key={it.id} item={it} onClick={() => setSelected(it)} />
        ))}
      </div>

      {/* Capture sheet */}
      {selected && (
        <CaptureSheet
          projectId={projectId}
          item={selected}
          currentUser={user}
          onClose={() => setSelected(null)}
          onSaved={(updated) => {
            setItems((prev) => prev.map((it) => (it.id === updated.id ? { ...it, ...updated } : it)));
            setSelected(null);
          }}
        />
      )}
    </div>
  );
}

function KpiMini({ label, value, total, color }) {
  return (
    <div className="panel p-3 text-center">
      <div className="font-display font-black text-2xl tabular-nums" style={{ color }}>{value}</div>
      <div className="label-mono text-[9px] text-slate-400 leading-tight">{label}</div>
      <div className="text-[10px] text-slate-500 mt-0.5">de {total}</div>
    </div>
  );
}

function ItemCard({ item, onClick }) {
  const pct = item.progress_pct_manual || 0;
  const isDone = pct >= 100;
  const color = isDone ? "#16a34a" : pct > 0 ? "#22d3ee" : "#64748b";
  return (
    <button
      data-testid={`progress-item-${item.id}`}
      onClick={onClick}
      className="w-full panel p-3 text-left active:scale-[0.99] transition-transform"
      style={{ borderLeft: `3px solid ${color}` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {item.code && <span className="label-mono text-[10px]" style={{ color: "var(--cyan)" }}>{item.code}</span>}
            {isDone && (
              <span className="label-mono text-[9px] flex items-center gap-0.5" style={{ color: "#16a34a" }}>
                <Check className="w-2.5 h-2.5" /> COMPLETADA
              </span>
            )}
            {item.progress_photo_id && (
              <ImageIcon className="w-3 h-3 text-slate-500" />
            )}
          </div>
          <div className="text-sm font-semibold text-slate-100 line-clamp-2">{item.description}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {item.quantity ? `${item.quantity} ${item.unit || ""}` : "—"} · {fmtMXN(item.amount || 0)}
          </div>
        </div>
        <div className="flex-shrink-0 flex items-center gap-2">
          <div className="text-right">
            <div className="font-mono font-bold tabular-nums" style={{ color, fontSize: "1.1rem" }}>{pct.toFixed(0)}%</div>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-600" />
        </div>
      </div>
      {/* Progress bar */}
      <div className="h-1.5 rounded-sm mt-2.5 overflow-hidden" style={{ background: "rgba(255,255,255,0.05)" }}>
        <div className="h-full transition-all" style={{ width: `${Math.min(100, pct)}%`, background: color }} />
      </div>
    </button>
  );
}

function CaptureSheet({ projectId, item, currentUser, onClose, onSaved }) {
  const current = item.progress_pct_manual || 0;
  const [pct, setPct] = useState(Math.round(current));
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const cameraRef = useRef();

  const handlePhoto = (file) => {
    if (!file) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setPhotoPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const bump = (delta) => setPct((p) => Math.min(100, Math.max(current, p + delta)));

  const isBelowCurrent = pct < current;

  const save = async () => {
    if (isBelowCurrent) {
      toast.error(`No puedes bajar el avance. El valor mínimo es ${current}%.`);
      return;
    }
    setSubmitting(true);
    try {
      let photoId = null;
      if (photoFile) {
        const fd = new FormData();
        fd.append("file", photoFile);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        photoId = data.id;
      }
      const { data: updated } = await api.post(
        `/projects/${projectId}/budget/${item.id}/progress`,
        { progress_pct: pct, photo_id: photoId },
      );
      toast.success(`Avance actualizado a ${pct}%`);
      onSaved(updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar el avance");
    } finally {
      setSubmitting(false);
    }
  };

  // Color preview
  const previewColor = pct >= 100 ? "#16a34a" : pct > 50 ? "#22d3ee" : "#a78bfa";

  return (
    <div
      data-testid="capture-sheet"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md rounded-t-2xl sm:rounded-md shadow-2xl"
        style={{
          background: "#0a0f1d",
          border: "1px solid var(--border-2)",
          maxHeight: "calc(100dvh - 1rem)",
          overflow: "auto",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle (mobile) */}
        <div className="sm:hidden flex justify-center pt-2">
          <div className="w-12 h-1 rounded-full" style={{ background: "rgba(255,255,255,0.2)" }} />
        </div>

        <div className="px-5 pt-4 pb-2 flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="label-mono text-[10px]" style={{ color: "var(--cyan)" }}>// CAPTURAR AVANCE</div>
            {item.code && <div className="label-mono text-[11px] mt-0.5" style={{ color: "var(--magenta)" }}>{item.code}</div>}
            <div className="font-display font-bold text-slate-100 text-base mt-1 leading-tight">
              {item.description}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              {item.quantity ? `${item.quantity} ${item.unit || ""}` : "—"} · {fmtMXN(item.amount || 0)}
            </div>
          </div>
          <button data-testid="capture-close" onClick={onClose} className="text-slate-400 hover:text-cyan-300 p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Big % display */}
        <div className="px-5 py-4 text-center">
          <div className="font-display font-black tabular-nums" style={{ color: previewColor, fontSize: "5rem", lineHeight: 1 }} data-testid="capture-pct-display">
            {pct}%
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Avance actual: <strong className="text-slate-300">{current.toFixed(0)}%</strong>
          </div>
        </div>

        {/* Slider */}
        <div className="px-5">
          <input
            type="range"
            min={current}
            max={100}
            step={1}
            value={Math.max(current, pct)}
            onChange={(e) => setPct(Number(e.target.value))}
            data-testid="capture-slider"
            className="w-full h-3 rounded-full appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, ${previewColor} 0%, ${previewColor} ${pct}%, rgba(255,255,255,0.1) ${pct}%, rgba(255,255,255,0.1) 100%)`,
              accentColor: previewColor,
            }}
          />
          <div className="flex items-center justify-between mt-1 text-[10px] text-slate-500 font-mono">
            <span>{current.toFixed(0)}% (mín)</span>
            <span>100%</span>
          </div>
        </div>

        {/* Quick buttons */}
        <div className="px-5 mt-3 grid grid-cols-4 gap-2">
          {[5, 10, 25, 50].map((d) => (
            <button
              key={d}
              data-testid={`capture-add-${d}`}
              onClick={() => bump(d)}
              className="btn-ghost py-2 text-xs uppercase tracking-widest font-semibold rounded-sm"
              disabled={pct >= 100}
            >
              +{d}%
            </button>
          ))}
        </div>
        <div className="px-5 mt-2 grid grid-cols-2 gap-2">
          <button
            data-testid="capture-set-100"
            onClick={() => setPct(100)}
            className="py-2 text-xs uppercase tracking-widest font-semibold rounded-sm"
            style={{ background: "#16a34a", color: "#0c1424" }}
          >
            Marcar 100% (Completada)
          </button>
          <button
            data-testid="capture-reset"
            onClick={() => setPct(Math.round(current))}
            className="btn-ghost py-2 text-xs uppercase tracking-widest font-semibold rounded-sm"
          >
            Restablecer
          </button>
        </div>

        {/* Photo */}
        <div className="px-5 mt-4">
          <div className="label-mono text-[10px] mb-1.5 text-slate-400">// EVIDENCIA FOTOGRÁFICA (opcional)</div>
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => handlePhoto(e.target.files?.[0])}
            className="hidden"
            data-testid="capture-camera-input"
          />
          {photoPreview ? (
            <div className="relative">
              <img src={photoPreview} alt="evidencia" className="w-full rounded-sm" style={{ maxHeight: 180, objectFit: "cover" }} />
              <button
                onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}
                className="absolute top-2 right-2 p-1.5 rounded-full"
                style={{ background: "rgba(0,0,0,0.7)" }}
                data-testid="capture-photo-remove"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          ) : (
            <button
              data-testid="capture-take-photo"
              onClick={() => cameraRef.current?.click()}
              className="w-full py-3 rounded-sm border border-dashed flex items-center justify-center gap-2 text-sm"
              style={{ borderColor: "var(--border-2)", color: "var(--cyan)" }}
            >
              <Camera className="w-4 h-4" /> Tomar foto del avance
            </button>
          )}
        </div>

        {/* Warning if trying to go below */}
        {isBelowCurrent && (
          <div className="px-5 mt-3">
            <div
              className="px-3 py-2 rounded-sm text-xs flex items-start gap-2"
              style={{ background: "rgba(220,38,38,0.12)", border: "1px solid rgba(220,38,38,0.4)", color: "#fca5a5" }}
            >
              <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                No puedes disminuir el avance ya capturado ({current.toFixed(0)}%). Si capturaste un valor incorrecto, contacta al administrador.
              </span>
            </div>
          </div>
        )}

        {/* Footer */}
        <div
          className="px-5 py-4 mt-4 flex gap-2 sticky bottom-0"
          style={{ background: "#0a0f1d", borderTop: "1px solid var(--border)" }}
        >
          <button
            data-testid="capture-cancel"
            type="button"
            onClick={onClose}
            className="flex-1 btn-ghost py-3 text-xs uppercase tracking-widest font-semibold rounded-sm"
          >
            Cancelar
          </button>
          <button
            data-testid="capture-save"
            type="button"
            onClick={save}
            disabled={submitting || isBelowCurrent || (pct === Math.round(current) && !photoFile)}
            className="flex-1 btn-primary py-3 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Guardando…</>
            ) : (
              <><Check className="w-4 h-4" /> Guardar avance</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
